import React, { useEffect, useState, useRef } from "react";
import moment from "moment";
import { useSelector } from "react-redux";
import { Box } from "@mui/material";
import ChartMargin from "./ChartMargin";
import ChartVolume from "./ChartVolume";
import ChartDeficit from "./ChartDeficit";
import ChartLoading from "../Components/ChartLoading";

import { useLazyQuery } from "@apollo/client";
import {
  MARGIN_HISTORY,
  SOLD_STOCKS,
  STOCK_DEFICIT,
} from "@/services/productService";

import { selectLocalization } from "@/store/store/selectors";

import {
  getFromToVariablesForWeekPicker,
  getFromToVariablesForMonthPicker,
} from "./helper";
import { MERCHANT_ID } from "@/constants/env";

const ALL_PRODUCT_OPTION = {
  id: "ALL",
  product_code: "ALL",
};
const PERIOD = 24 * 3600;

const ChartLayout = ({ store, refetchVolume, product }) => {
  const localization = useSelector(selectLocalization);
  const currentWeek = useRef(moment().isoWeek());
  const DEFAULT_WEEK = currentWeek.current;
  const DEFAULT_WEEK_TO_COMPARE = DEFAULT_WEEK - 1 || DEFAULT_WEEK;
  const DEFAULT_DATE_RANGE = [
    moment().subtract(7, "days").startOf("day"),
    moment().endOf("day"),
  ];
  const DEFAULT_MONTH = moment().month();

  const [marginHistory, setMarginHistory] = useState([]);
  const [mainSoldStocks, setMainSoldStocks] = useState([]);
  const [compareSoldStocks, setCompareSoldStocks] = useState([]);
  const [deficit, setDeficit] = useState([]);

  const [hasLoadedMargin, setHasLoadedMargin] = useState(false);
  const [hasLoadedSoldStocks, setHasLoadedSoldStocks] = useState(false);
  const [hasLoadedDeficit, setHasLoadedDeficit] = useState(false);

  const [getMarginHistory] = useLazyQuery(MARGIN_HISTORY, {
    onCompleted: (data) => {
      setMarginHistory(data.margin_history);
      setHasLoadedMargin(true);
    },
    onError: () => setHasLoadedMargin(true),
    fetchPolicy: "no-cache",
  });

  const [getSoldStocks] = useLazyQuery(SOLD_STOCKS, {
    onCompleted: (data) => {
      setMainSoldStocks(data.main_data);
      setCompareSoldStocks(data.compare_data);
      setHasLoadedSoldStocks(true);
    },
    onError: () => setHasLoadedSoldStocks(true),
    fetchPolicy: "no-cache",
  });

  const [getStockDeficit] = useLazyQuery(STOCK_DEFICIT, {
    onCompleted: (data) => {
      setDeficit(data.stock_deficit);
      setHasLoadedDeficit(true);
    },
    onError: () => setHasLoadedSoldStocks(true),
    fetchPolicy: "no-cache",
  });

  const getProductIds = (_product) => {
    if (_product.id === ALL_PRODUCT_OPTION.id) {
      return store.products?.map((item) => item.id);
    }
    return [_product.id];
  };

  const refetchQueries = () => {
    setHasLoadedMargin(false);
    setHasLoadedSoldStocks(false);
    getMarginHistory({
      context: {
        headers: {
          merchantId: MERCHANT_ID,
          storeId: store.id,
        },
      },
      variables: {
        product_ids: getProductIds(product),
        from: moment(DEFAULT_DATE_RANGE[0]).toISOString().replace("Z", ""),
        to: moment(DEFAULT_DATE_RANGE[1]).toISOString().replace("Z", ""),
      },
    });
    const fromToVars = getFromToVariablesForWeekPicker(
      DEFAULT_WEEK,
      DEFAULT_WEEK_TO_COMPARE
    );

    getSoldStocks({
      context: {
        headers: {
          merchantId: MERCHANT_ID,
          storeId: store.id,
        },
      },
      variables: {
        product_ids: getProductIds(product),
        period: PERIOD,
        from: fromToVars.from,
        to: fromToVars.to,
        fromCompare: fromToVars.fromCompare,
        toCompare: fromToVars.toCompare,
      },
    });

    getStockDeficit({
      context: {
        headers: {
          merchantId: MERCHANT_ID,
          storeId: store.id,
        },
      },
      variables: {
        product_ids: getProductIds(product),
        from: getFromToVariablesForMonthPicker(DEFAULT_MONTH).from,
        to: getFromToVariablesForMonthPicker(DEFAULT_MONTH).to,
        period: PERIOD,
      },
    });
  };

  useEffect(() => {
    if (!!store?.id) {
      refetchQueries();
    }
  }, [store?.id, product]);

  const onChangeMarginData = (marginData) => {
    setMarginHistory(
      marginHistory.map((item) =>
        item?.product?.id === marginData?.product?.id ? marginData : item
      )
    );
  };

  const onChangeStockData = (mainData, compareData) => {
    setMainSoldStocks(
      mainSoldStocks.map((item) =>
        item?.product?.id === mainData?.product?.id ? mainData : item
      )
    );
    setCompareSoldStocks(
      compareSoldStocks.map((item) =>
        item?.product?.id === compareData?.product?.id ? compareData : item
      )
    );
  };

  const onChangeStockDeficit = (deficitData) => {
    setDeficit(deficitData);
  };

  return (
    <div>
      {hasLoadedMargin && hasLoadedSoldStocks && hasLoadedDeficit ? (
        <>
          {/* <Box mt={10}>
            <ChartDeficit
              defaultMonth={DEFAULT_MONTH}
              store={store}
              storeProducts={store.products}
              product={product}
              isAllProducts={product === ALL_PRODUCT_OPTION}
              onChangeStockDeficit={onChangeStockDeficit}
              deficit={deficit?.filter(
                (item) => moment(item.to) < moment().endOf("day")
              )}
            />
          </Box> */}
          {marginHistory.map((marginData, id) => (
            <Box key={`margin-${id}`} mt={10}>
              <ChartMargin
                store={store}
                product={marginData.product}
                localization={localization}
                marginData={marginData.margins}
                defaultDateRange={DEFAULT_DATE_RANGE}
                onChangeMarginData={onChangeMarginData}
              />
              <Box mt={10}>
                <ChartVolume
                  refetchVolume={refetchVolume}
                  store={store}
                  product={mainSoldStocks[id]?.product}
                  main_data={mainSoldStocks[id]?.sold_stocks}
                  compare_data={compareSoldStocks[id]?.sold_stocks}
                  localization={localization}
                  defaultWeek={DEFAULT_WEEK}
                  defaultWeekToCompare={DEFAULT_WEEK_TO_COMPARE}
                  onChangeStockData={onChangeStockData}
                />
              </Box>
            </Box>
          ))}
        </>
      ) : (
        <Box mt={10}>
          <ChartLoading />
        </Box>
      )}
    </div>
  );
};

export default ChartLayout;
